/* Kit builder: switch user-type kits, keep a running total, add the ticked items in one call.
   Rows: [data-kit-item] checkboxes (value = variant id, data-price in cents, data-qty). Extras:
   * a row with several sizes has a select [data-size-select]; the pick sets the row's variant id, price and stock line
   * a bundle row ("Make it 30 m", the maintenance pack) has data-ids (comma list) and data-price as the bundle price, data-was the RRP total
   * a "Keep it running" spare row has data-kit-spare and data-kit-discount (percent off RRP with the sprayer, shown here, applied by the
     Shopify automatic discount); the pack and its parts are exclusive
   * an extra airless hose row has data-kit-hose="1/4|3/8"; its hidden coupler row [data-kit-joiner] appears, ticks and takes the quantity
   * a Job ready panel has data-job-pct and data-job-ids (variant ids): that percent comes off every ticked item in the list, one unit of
     each (the sprayer is always in the kit, and the job-ready-kits function gives the add-ons pct with a sprayer of the class, partial
     kits included, capped at the kit quantity per sprayer), and never off the sprayer
   * a tip row has data-kit-family and data-two-pack: 2 or more tips of one family ticked take that percent off those tips. A line that
     two offers reach gets the larger one, which is what Shopify does at the cart
   * a panel with data-bundle-buy / data-bundle-get / data-bundle-pct (Second painter) shows the handpiece saving while both are ticked
   * [data-kit-spares] data-gift-variant: the free Pump Armor ("Free Pump Armor with your sprayer", one per order). The sprayer is always in
     the kit, so one bottle of that variant counts $0 (its row says so) and any further bottle takes the spares percentage, as at the cart.
     The pack's price already has the bottle at $0 (kit-spares.liquid) */
(() => {
  document.querySelectorAll('[data-kit-builder]').forEach((root) => {
    const $$ = (s) => Array.from(root.querySelectorAll(s));
    const total = root.querySelector('[data-kit-total]');
    const count = root.querySelector('[data-kit-count]');
    const addBtn = root.querySelector('[data-kit-add]');
    const saving = root.querySelector('[data-kit-saving]');
    const panel = () => root.querySelector('[data-kit-panel]:not([hidden])');
    const money = (c) => window.ASE.formatMoney(c);
    const scopes = () => [panel(), root.querySelector('[data-kit-spares]')].filter(Boolean);
    const ticked = () => scopes().flatMap((sc) => Array.from(sc.querySelectorAll('[data-kit-item]:checked')));
    const unitOf = (i) => {
      const price = Number(i.dataset.price || 0);
      const row = i.closest('[data-kit-spare]');
      if (!row) return price;
      return price - Math.round((price * Number(row.dataset.kitDiscount || 0)) / 100);
    };
    const rrpOf = (i) => Number(i.dataset.was || i.dataset.price || 0);
    const giftId = root.querySelector('[data-kit-spares]')?.dataset.giftVariant || '';
    const giftBox = () => {
      if (!giftId) return null;
      const g = root.querySelector('[data-kit-spares]');
      return Array.from(g.querySelectorAll('[data-kit-item]')).find((i) => i.value === giftId && !i.dataset.ids) || null;
    };
    /* Label the gift row once: struck RRP, $0, "Free with your sprayer" */
    (() => {
      const cb = giftBox();
      if (!cb) return;
      const row = cb.closest('[data-kit-row]');
      const box = row.querySelector('.kit-item__price');
      const pr = row.querySelector('[data-kit-price]');
      if (!box || !pr) return;
      if (!row.querySelector('[data-kit-was]')) { const was = document.createElement('s'); was.className = 'kit-item__was'; was.dataset.kitWas = ''; box.insertBefore(was, pr); }
      row.querySelector('[data-kit-was]').textContent = money(Number(cb.dataset.price || 0));
      pr.textContent = money(0);
      let off = box.querySelector('.kit-item__offer');
      if (!off) { off = document.createElement('span'); off.className = 'kit-item__offer'; box.appendChild(off); }
      off.textContent = 'Free with your sprayer';
      const al = cb.getAttribute('aria-label') || ''; cb.setAttribute('aria-label', `${al.slice(0, al.lastIndexOf(',') > 0 ? al.lastIndexOf(',') : al.length)}, free with your sprayer`);
    })();

    const syncJoiners = () => {
      const p = panel();
      if (!p) return;
      p.querySelectorAll('[data-kit-joiner]').forEach((w) => {
        const size = w.dataset.kitJoiner;
        const n = p.querySelectorAll(`[data-kit-hose="${size}"] [data-kit-item]:checked`).length;
        const cb = w.querySelector('[data-kit-item]');
        const was = !w.hidden;
        w.hidden = n === 0;
        if (n === 0) { cb.checked = false; cb.dataset.qty = '1'; delete cb.dataset.touched; }
        else {
          cb.dataset.qty = String(n);
          if (!was && !cb.dataset.touched) cb.checked = true;
        }
        const q = w.querySelector('[data-kit-qty]');
        if (q) q.textContent = n > 1 ? `x ${n}` : '';
      });
    };

    const syncPack = () => {
      const g = root.querySelector('[data-kit-spares]');
      if (!g) return;
      const pack = g.querySelector('[data-kit-pack] [data-kit-item]');
      const parts = Array.from(g.querySelectorAll('[data-kit-spare] [data-kit-item]'));
      if (!pack) return;
      parts.forEach((i) => {
        if (pack.checked) i.checked = false;
        i.disabled = pack.checked;
        i.closest('[data-kit-row]').classList.toggle('is-off', pack.checked);
      });
    };

    /* Job ready: which kit ids are ticked (any of them qualifies; complete = every one ticked, for the note) */
    const jobState = (p) => {
      if (!p || !p.dataset.jobIds) return null;
      const ids = p.dataset.jobIds.split(',');
      const have = new Set(Array.from(p.querySelectorAll('[data-kit-item]:checked')).filter((c) => !c.disabled).map((c) => c.value));
      return { ids: new Set(ids), pct: Number(p.dataset.jobPct), complete: ids.every((id) => have.has(id)), missing: ids.filter((id) => !have.has(id)).length };
    };
    /* What the automatic discounts take off the ticked lines (cents). Per line the larger of the kit and the two pack applies. */
    const offerSaving = (on) => {
      const p = panel(), job = jobState(p), fam = {};
      on.forEach((i) => {
        const row = i.closest('[data-kit-family]');
        if (row && !i.dataset.ids) fam[row.dataset.kitFamily] = (fam[row.dataset.kitFamily] || 0) + Number(i.dataset.qty || 1);
      });
      let save = 0;
      on.forEach((i) => {
        if (i.dataset.ids || i.closest('[data-kit-spare]')) return;
        const q = Number(i.dataset.qty || 1), line = Number(i.dataset.price || 0) * q;
        let pct = 0;
        const row = i.closest('[data-kit-family]');
        const two = row && fam[row.dataset.kitFamily] >= 2 ? Number(row.dataset.twoPack) : 0;
        // the kit covers one unit of each add-on per sprayer, so the cart splits a line of 2: one unit takes the larger of the
        // kit and the two pack, the rest take the two pack (tested 18 Oct 2026: FFLP514 x2 = 15% on one, 7% on the other)
        const unit = Number(i.dataset.price || 0);
        if (job && job.ids.has(i.value)) save += Math.max(Math.round((unit * job.pct) / 100), Math.round((unit * two) / 100)) + Math.round((unit * (q - 1) * two) / 100);
        else save += Math.round((line * two) / 100);
      });
      return save;
    };

    /* Delivered to <postcode>: the kit total shown above plus the freight the quote endpoint (Rule E, the same function as the checkout
       carrier service) gives for exactly the ticked items. Not the cart. Hidden if the quote fails, never guessed. */
    const dEl = root.querySelector('[data-kit-delivered]');
    let dSeq = 0;
    const kitLines = () => {
      const lines = [];
      ticked().forEach((i) => {
        const q = Number(i.dataset.qty || 1);
        if (i.dataset.lines) {
          let arr = [];
          try { arr = JSON.parse(i.dataset.lines); } catch (e) { arr = []; }
          arr.forEach((l) => lines.push({ sku: l.sku || '', quantity: q, grams: Number(l.grams) || 0, price: Number(l.price) || 0, product_type: l.product_type || '' }));
        } else {
          lines.push({ sku: i.dataset.sku || '', quantity: q, grams: Number(i.dataset.grams) || 0, price: Number(i.dataset.price) || 0, product_type: i.dataset.ptype || '' });
        }
      });
      return lines;
    };
    const deliveredUpdate = (cents) => {
      if (!dEl) return;
      const fc = window.ASE_FREIGHT;
      const seq = ++dSeq;
      if (!fc || !fc.endpoint()) { dEl.hidden = true; return; }
      const pc = fc.get();
      if (!pc) {
        dEl.hidden = false;
        dEl.innerHTML = '<button type="button" class="fcalc__prompt" data-fcalc-focus>Enter your postcode for a delivered price</button>';
        return;
      }
      const lines = kitLines();
      if (!lines.length) { dEl.hidden = true; return; }
      fc.quote(fc.endpoint(), pc, lines).then((q) => {
        if (seq !== dSeq) return;
        /* Parcel freight is free from the threshold on the order subtotal. The quote works on list prices; if the kit's own savings
           carry it from over to under the threshold we cannot say which side checkout lands on, so show nothing rather than guess. */
        const thr = Number(document.querySelector('[data-fcalc]')?.dataset.threshold) || 0;
        const parcelOnly = !(q.breakdown && q.breakdown.heavy_units > 0);
        if (parcelOnly && thr > 0 && (q.subtotal_cents >= thr) !== (cents >= thr)) { dEl.hidden = true; dEl.innerHTML = ''; return; }
        dEl.hidden = false;
        dEl.innerHTML = `<p class="kit__dtotal">Delivered to ${pc}: <strong>${money(cents + q.total_cents)}</strong> inc GST</p>` +
          `<p class="kit__dnote">The kit ${money(cents)}${q.total_cents === 0 ? ' with free freight' : ` plus ${money(q.total_cents)} freight`}, for the ticked items only, not your cart. Estimated, confirmed at checkout.</p>`;
      }, () => { if (seq === dSeq) { dEl.hidden = true; dEl.innerHTML = ''; } });
    };
    document.addEventListener('ase:postcode', () => recalc());
    document.addEventListener('ase:fc-ready', () => recalc());

    const recalc = () => {
      syncJoiners();
      syncPack();
      const on = ticked();
      let cents = 0, rrp = 0, n = 0;
      on.forEach((i) => {
        const q = Number(i.dataset.qty || 1);
        cents += unitOf(i) * q; rrp += rrpOf(i) * q; n += q;
      });
      cents -= offerSaving(on);
      const gb = giftBox();
      if (gb && gb.checked && !gb.disabled) cents -= unitOf(gb); // one bottle free; the rest stay at the spares price
      const jn = root.querySelector('[data-kit-jobnote]');
      if (jn) {
        const job = jobState(panel());
        jn.hidden = !job;
        if (job) jn.textContent = `Job ready kit: ${job.pct}% off the add-ons with your sprayer, not the sprayer itself.`;
      }
      const p = panel();
      let bundleSave = 0;
      if (p && p.dataset.bundleBuy && p.dataset.bundleGet) {
        const buy = p.querySelector(`[data-kit-item][value="${p.dataset.bundleBuy}"]`);
        const get = p.querySelector(`[data-kit-item][value="${p.dataset.bundleGet}"]`);
        if (buy && get && buy.checked && get.checked) bundleSave = Math.floor(Number(get.dataset.price) * Number(p.dataset.bundlePct));
      }
      cents -= bundleSave;
      total.innerHTML = (rrp > cents ? `<s>${money(rrp)}</s>` : '') + money(cents);
      count.textContent = n;
      const unit = root.querySelector('[data-kit-unit]');
      if (unit) unit.textContent = n === 1 ? 'item' : 'items';
      syncQuote();
      deliveredUpdate(cents);
      if (saving) {
        const save = rrp - cents;
        saving.hidden = save <= 0;
        if (save > 0) saving.textContent = `You save ${money(save)} against RRP.`;
      }
    };

    $$('[data-kit-tab]').forEach((tab) => tab.addEventListener('click', () => {
      $$('[data-kit-tab]').forEach((t) => t.setAttribute('aria-pressed', String(t === tab)));
      $$('[data-kit-panel]').forEach((p) => { p.hidden = p.dataset.kitPanel !== tab.dataset.kitTab; });
      recalc();
    }));

    /* Links elsewhere on the page ([data-kit-open="interior"]) switch to that Job ready tab; the link's #kit does the scrolling */
    document.addEventListener('click', (e) => {
      const a = e.target.closest('[data-kit-open]');
      if (!a) return;
      const tab = root.querySelector(`[data-kit-tab][data-kit-key="${a.dataset.kitOpen}"]`);
      if (tab) tab.click();
    });

    root.addEventListener('change', (e) => {
      const t = e.target;
      if (t.matches('[data-size-select]')) {
        const row = t.closest('[data-kit-row]');
        const cb = row.querySelector('[data-kit-item]');
        const o = t.selectedOptions[0];
        cb.value = o.value;
        cb.dataset.price = o.dataset.price;
        if (o.dataset.grams) cb.dataset.grams = o.dataset.grams;
        if (o.dataset.sku) cb.dataset.sku = o.dataset.sku;
        if (o.dataset.label) cb.setAttribute('aria-label', o.dataset.label);
        const pr = row.querySelector('[data-kit-price]');
        if (pr) pr.textContent = money(unitOf(cb));
        const was = row.querySelector('[data-kit-was]');
        if (was) was.textContent = money(Number(o.dataset.price));
        const st = row.querySelector('[data-kit-stock]');
        if (st && o.dataset.stock) st.innerHTML = o.dataset.stock;
        if (!cb.disabled && !cb.checked) cb.checked = true;
        recalc();
      } else if (t.matches('[data-kit-item]')) {
        if (t.closest('[data-kit-joiner]')) t.dataset.touched = '1';
        recalc();
      }
    });

    addBtn.addEventListener('click', () => {
      const items = [];
      ticked().forEach((i) => {
        const q = Number(i.dataset.qty || 1);
        const ids = i.dataset.ids ? i.dataset.ids.split(',') : [i.value];
        ids.forEach((id) => items.push({ id: Number(id), quantity: q }));
      });
      window.ASE.addWithFeedback(addBtn, items);
    });

    /* Quote me this kit: /pages/contact with ?machine= and ?kit= (the ticked items as plain text, titles and SKUs, no prices). The contact
       form (sections/contact-email.liquid) puts the list in the message. A tip family row names the size picked in its select; a tip
       variant's title is its SKU. */
    const quoteLink = root.querySelector('[data-kit-quote]');
    const kitText = () => ticked().map((i) => {
      const row = i.closest('[data-kit-row]');
      const sel = row && row.querySelector('[data-size-select]');
      const nameEl = row && row.querySelector('.part-row__name');
      const name = (i.dataset.title || (nameEl ? nameEl.firstChild.textContent : '') || '').trim();
      const size = sel && sel.selectedOptions[0] ? sel.selectedOptions[0].textContent.split(' \u00b7 ')[0].trim() : '';
      const sku = size || (i.dataset.sku || '').trim();
      const q = Number(i.dataset.qty || 1);
      return `${q > 1 ? `${q} x ` : ''}${name}${sku && sku !== name ? ` (${sku})` : ''}`;
    }).join('\n');
    const syncQuote = () => {
      if (!quoteLink) return;
      const q = new URLSearchParams();
      if (quoteLink.dataset.machine) q.set('machine', quoteLink.dataset.machine);
      const t = kitText();
      if (t) q.set('kit', t);
      quoteLink.href = `${quoteLink.dataset.base || '/pages/contact'}?${q.toString()}`;
    };
    if (quoteLink) quoteLink.addEventListener('click', syncQuote);

    /* Tip family rows: a long size list collapses to the recommended sizes (the size that ships with the sprayer, the sprayer's tip picks)
       with a More sizes button that puts the whole list back. Without JS the full list shows. */
    const picks = new Set((root.dataset.tipPicks || '').split(',').filter(Boolean));
    $$('[data-size-select]').forEach((sel) => {
      const all = Array.from(sel.options);
      if (all.length < 7) return;
      const keep = all.filter((o) => o.selected || picks.has(o.value));
      if (!keep.length || keep.length >= all.length) return;
      Array.from(sel.options).forEach((o) => { if (!keep.includes(o)) o.remove(); });
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'kit-item__more';
      more.textContent = `More sizes (${all.length - keep.length})`;
      more.addEventListener('click', () => {
        const cur = sel.value;
        sel.innerHTML = '';
        all.forEach((o) => sel.appendChild(o));
        sel.value = cur;
        more.remove();
      });
      sel.insertAdjacentElement('afterend', more);
    });

    /* A size select is the source of truth for its row on load (the sprayer's own size, sizes it can run). */
    $$('[data-size-select]').forEach((sel) => {
      const row = sel.closest('[data-kit-row]');
      const cb = row.querySelector('[data-kit-item]');
      const o = sel.selectedOptions[0];
      if (!o) return;
      cb.value = o.value;
      cb.dataset.price = o.dataset.price;
      if (o.dataset.grams) cb.dataset.grams = o.dataset.grams;
      if (o.dataset.sku) cb.dataset.sku = o.dataset.sku;
      if (o.dataset.label) cb.setAttribute('aria-label', o.dataset.label);
      const pr = row.querySelector('[data-kit-price]');
      if (pr) pr.textContent = money(unitOf(cb));
      const st = row.querySelector('[data-kit-stock]');
      if (st && o.dataset.stock) st.innerHTML = o.dataset.stock;
    });

    recalc();
  });
})();
